import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, CircularProgress, IconButton, Snackbar, Stack, Switch, Tooltip, Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import AdsApi, { EXPORTS } from '../api/ads';
import { DEFAULT_SITE, SiteFilter, StatusFilter, listUrl } from '../components/ads/AdFilters';
import { METRIC_COLUMNS, NameCell } from '../components/ads/cells';
import SyncNowButton from '../components/connections/SyncNowButton';
import StatusBadge from '../components/status/StatusBadge';
import { SelectFilter } from '../components/filters/Filters';
import ListPage from '../components/table/ListPage';
import { formatDate, fromNow } from '../utils/dates';
import { formatNumber } from '../utils/format';

const DEFAULTS = { site: DEFAULT_SITE };
// how often the page checks on a sync in progress, in milliseconds
const SYNC_POLL = 5000;
const SYNC_OPTIONS = [{ value: 'enabled', label: 'Sync on' }, { value: 'disabled', label: 'Sync off' }];

// session.permissions.can_manage_sync false (the user's role in the iConnect account): the Sync switches are off
export default function AdAccounts({ session }) {
  const canManageSync = session?.permissions?.can_manage_sync !== false;
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  const toggleSync = async (row, { patchRow }) => {
    patchRow(row.id, { is_enabled: !row.is_enabled });
    try {
      await AdsApi.updateAccount(row.id, { is_enabled: !row.is_enabled });
    } catch (e) {
      patchRow(row.id, { is_enabled: row.is_enabled });
      setError(`Could not update ${row.name}: ${e.message}`);
    }
  };

  const syncNow = async (row, { patchRow }) => {
    patchRow(row.id, { is_syncing: true, last_sync_failed: false });
    try {
      await AdsApi.syncAccount(row.id);
    } catch (e) {
      patchRow(row.id, { is_syncing: e.status === 409 });
      if (e.status !== 409) setError(`Could not sync ${row.name}: ${e.message}`);
    }
  };

  const columns = (list) => [
    {
      id: 'name', label: 'Ad account',
      render: (row) => <NameCell name={row.name} secondary={[row.act_id, row.currency].filter(Boolean).join(' · ')} />,
    },
    {
      id: 'is_enabled', label: 'Sync', render: (row) => (
        <Tooltip title={!canManageSync ? `${row.is_enabled ? 'Synced' : 'Not synced'}; your role cannot change this`
          : row.is_enabled ? 'Synced; turn off to stop syncing' : 'Not synced; turn on to sync'}>
          {/* a span, so the tooltip still shows on a disabled switch; clicks stay off the row */}
          <span onClick={(event) => event.stopPropagation()}>
            <Switch size="small" checked={row.is_enabled} disabled={!canManageSync}
                    onChange={() => toggleSync(row, list)} inputProps={{ 'aria-label': `Sync ${row.name}` }} />
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'account_status', label: 'Status', sort: false, render: (row) => <AccountStatus row={row} />,
    },
    { id: 'campaign_count', label: 'Campaigns', align: 'right', render: (row) => formatNumber(row.campaign_count) },
    { id: 'ad_count', label: 'Ads', align: 'right', render: (row) => formatNumber(row.ad_count) },
    ...METRIC_COLUMNS.filter((column) => ['spend', 'impressions', 'clicks', 'ctr'].includes(column.id)),
    {
      id: 'last_synced_at', label: 'Last synced', render: (row) => <LastSynced row={row} />,
    },
    { id: 'connection_name', label: 'Connected by', sort: false },
    ...(canManageSync ? [{
      id: 'actions', label: 'Actions', sort: false, render: (row) => (
        <Tooltip title={!row.is_enabled ? 'Turn on sync to sync this ad account'
          : row.is_syncing ? 'Syncing' : 'Sync campaigns, ad sets, ads and insights'}>
          <span onClick={(event) => event.stopPropagation()}>
            <IconButton size="small" aria-label={`Sync ${row.name} now`} disabled={!row.is_enabled || row.is_syncing}
                        onClick={() => syncNow(row, list)}>
              {row.is_syncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      ),
    }] : []),
  ];

  return (
    <>
      <ListPage
        exportPath={EXPORTS.accounts}
        actions={(list) => (
          <>
            <SyncPoller list={list} />
            {canManageSync && <SyncNowButton onDone={list.refresh} onError={setError} />}
          </>
        )}
        title="Ad accounts" description="Ad accounts reachable through your connections. Choose which ones are synced."
        fetchList={AdsApi.accounts} defaults={DEFAULTS} columns={columns}
        onRowClick={(row, query) => navigate(listUrl('/campaigns', query, { ad_account: row.id }))}
        emptyTitle="No ad accounts yet" emptyHint="Connect an account on the Connections page, then sync ad accounts."
        searchPlaceholder="Search name or act_ id"
        filters={({ query, setQuery }) => (
          <>
            <SiteFilter query={query} setQuery={setQuery} />
            <StatusFilter query={query} setQuery={setQuery} type="ad_account" />
            <SelectFilter label="Sync" value={query.sync} options={SYNC_OPTIONS} allLabel="All accounts" width={180}
                          onChange={(sync) => setQuery({ sync })} />
          </>
        )}
      />
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)} variant="filled">{error}</Alert>
      </Snackbar>
    </>
  );
}

// While any row on the page is syncing, keeps the table up to date
function SyncPoller({ list }) {
  const { refresh, rows } = list;
  const syncing = rows.some((row) => row.is_syncing);

  useEffect(() => {
    if (!syncing) return undefined;
    const timer = setInterval(refresh, SYNC_POLL);
    return () => clearInterval(timer);
  }, [syncing, refresh]);

  return null;
}

function LastSynced({ row }) {
  return (
    <Stack spacing={0.25}>
      {row.last_synced_at ? (
        <Typography variant="body2" title={formatDate(row.last_synced_at)}>{fromNow(row.last_synced_at)}</Typography>
      ) : <Typography variant="body2" color="text.secondary">Never</Typography>}
      {row.is_syncing && <Typography variant="caption" color="text.secondary">Syncing…</Typography>}
      {!row.is_syncing && row.last_sync_failed && (
        <Typography variant="caption" color="error">Last sync failed</Typography>
      )}
    </Stack>
  );
}

// label and tone come from the API (social.constants.AD_ACCOUNT_STATUSES)
function AccountStatus({ row }) {
  if (!row.account_status_label) return '—';
  return <StatusBadge tone={row.account_status_tone} label={row.account_status_label} />;
}
